import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect, useRef, Suspense } from "react";
import { Route, Routes as RouterRoutes, useParams } from "react-router-dom";

import routes from "./routes";
import { getChangedFields, Portal } from "../../../../util";
import DetailBar from "./components/DetailBar";
import AuthRoute from "../../../../components/AuthRoute";
import { NavBar, Spinner } from "../../../../components/ui";
import { FETCH_INSTANCE as fetchInstance } from "../../../../graphql/query/Instance";
import { UPDATE_INSTANCE as updateInstanceMutation } from "../../../../graphql/mutation/Instance";
import { getInstanceLoadErrorMessage } from "./instanceLoadState";

const Body = props => {
  const { loading, ...remainingProps } = props || {};

  return loading ? (
    <div className="h-100% flex w-full items-center justify-center">
      <Spinner />
    </div>
  ) : (
    <RouterRoutes>
      {routes
        ?.filter?.(item => item?.element)
        ?.map((item, index) => {
          return (
            <Route
              key={index}
              path={item?.href}
              element={
                <AuthRoute permissions={item.permissions}>
                  <Suspense fallback={<Spinner />}>
                    <item.element {...remainingProps} />
                  </Suspense>
                </AuthRoute>
              }
            />
          );
        })}
    </RouterRoutes>
  );
};

const Wizard = props => {
  const abortController = useRef();
  const { id: instanceId } = useParams();
  const {
    data: { instance } = {},
    loading,
    error,
  } = useQuery(
    fetchInstance({
      artistRef: true, // For Details tab
      articheckUrl: true, // for Condition tab
      artsy: true, // For Details tab
      bondType: true,
      channelEcommerce: true, // For Details tab
      channelPresentation: true, // For Details tab
      company: { code: true, name: true }, // Explicitly include code field for Control Ref display
      condition: { status: true },
      createdAt: true, // For Details tab
      description: true,
      editionNumber: true,
      editionNumberFormatted: true,
      edition: {
        ap: true, // For Details tab (Edition Type)
        number: true, // For Details tab
        year: true, // For Details tab
        artwork: {
          artists: {
            name: true, // For Details tab artist display
            droitdesuite: true, // For Details tab
          },
          categoryId: true, // For Details tab
          description: true, // For Details tab
          dimensions: true, // For Details tab
          goodsCode: true, // For Details tab
          images: { imgT: true, imgX: true, order: true }, // Full images for Details tab
          medium: true, // For Details tab
          photoLink: true, // For Details tab
          series: true, // For Details tab
          title: true, // For Details tab
          videoLink: true, // For Details tab,
          installationLink: true,
        },
      },
      fabricated: true,
      fabricatedDate: true,
      framed: true, // For Details tab
      ipad: true, // For Details tab (Views)
      isBond: true,
      isGuaranteed: true,
      isOnLoan: true,
      isSecondaryMarket: true,
      location: true,
      locationHistory: true, // For History tab
      market: true, // For InstanceTags
      notes: true, // For DetailBar display
      ref: true, // For Details tab (Control Ref)
      reference: true,
      salesStrategy: true, // For History tab
      status: true,
      statusHistory: true, // For History tab
      statusId: true, // For PrimaryNav actions
      unpackedWeight: true,
      weight: true, // For DetailBar display
      warehouseItem: {
        warehouseMoves: {
          location: true,
        },
        itemSite: {
          location: true,
        },
      },
      returns: true, // For History tab
      returnsDirector: true, // For History tab
      parentOnline: true, // For History tab
      forSale: true, // For History tab
      certificate: true, // For COA tab
      certificateSent: true, // For COA tab
    }),
    { skip: !instanceId, variables: { id: +instanceId } },
  );

  const [updateInstance] = useMutation(
    updateInstanceMutation({
      artistRef: true,
      artsy: true,
      channelEcommerce: true,
      channelPresentation: true,
      company: {},
      consigner: {
        contactCompany: {
          images: {},
        },
      },
      contact: {},
      condition: { status: true },
      createdAt: true,
      edition: {
        ap: true,
        number: true,
        year: true,
        artwork: {
          artists: { droitdesuite: true },
          categoryId: true,
          description: true,
          dimensions: true,
          goodsCode: true,
          images: { imgT: {}, imgX: {} },
          medium: true,
          photoLink: true,
          series: true,
          title: true,
          videoLink: true,
        },
      },
      framed: true,
      ipad: true,
      isBond: true,
      isGuaranteed: true,
      isOnLoan: true,
      isSecondaryMarket: true,
      notes: true,
      ref: true,
      salePrice: {},
      warehouseItem: {
        warehouseMoves: {
          location: {},
        },
        itemSite: {
          location: {},
        },
      },
      weight: {},
    }),
  );

  const formik = useFormik({
    initialValues: instance || {},
    enableReinitialize: true,
    onSubmit: values => {
      const changedFields = getChangedFields(values, instance || {});

      if (Object.keys(changedFields).length > 0) {
        const controller = new AbortController();
        abortController.current = controller;

        updateInstance({
          variables: {
            input: {
              id: +instanceId,
              ...changedFields,
            },
          },
          context: {
            fetchOptions: {
              signal: controller.signal,
            },
          },
        });
      }
    },
  });

  useEffect(() => {
    if (formik?.values && abortController?.current) {
      abortController.current.abort();
    }
  }, [formik?.values]);

  const loadErrorMessage = getInstanceLoadErrorMessage({
    error,
    instance,
    loading,
  });

  // Handle missing/error state and avoid rendering detail content.
  if (loadErrorMessage) {
    return (
      <div className="relative flex min-h-full items-center justify-center">
        <div className="text-red-600">{loadErrorMessage}</div>
      </div>
    );
  }

  const bodyProps = {
    ...props,
    formik,
    instance: instance || {},
    instanceId: +instanceId,
    loading,
  };

  return (
    <div className="relative flex min-h-full">
      <Portal id="sidebar">
        <NavBar
          navigation={routes?.filter?.(item => item?.icon)}
          open={props?.open}
        />
      </Portal>
      <DetailBar {...bodyProps} />
      <Body {...bodyProps} />
    </div>
  );
};

export default Wizard;
